import React, {Fragment, useEffect, useState} from "react";
import {Card, CardBody, CardFooter, Col, Row} from "reactstrap";
import {useDispatch} from "react-redux";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {cloneDeep, isEmpty} from "lodash";
import {
  requestSensorDetails,
  updateSensorDetailsConfig,
} from "../../../redux/Actions/controllers.actions";
import LeftNav from "../../../components/LeftNav/LeftNav";
import AutoForm from "../../../components/Forms/AutoForm";
import EmptyView from "../../../components/DataManagement/Empty";
import SafeView from "../../../components/safeView/SafeView";
import SensorConfigButtons from "./SensorConfigButtons";
import {filterFieldDefinitions} from "./utils/filterFieldDefinitions";
import {filterPageSections} from "./utils/filterPageSections";
import "./SensorDetails.scss";

const SensorDetails = () => {
  const initialState = {sensor: {metadata: []}};
  const [sensorConfig, setSensorConfig] = useState(initialState);
  const {sensor = {}} = sensorConfig;
  const {metadata = []} = sensor;

  const navigate = useNavigate();
  const location = useLocation();
  const routeAction = location?.state?.action;
  const {sensorId} = useParams();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(requestSensorDetails(sensorId, setSensorConfig, routeAction));
  }, [dispatch, routeAction, sensorId]);

  const inputChangeListener = (event, pageSectionSlug, metaDataIndex) => {
    const {accessKey: inputIndex, value, type, checked} = event.target;

    const newMetadata = cloneDeep(metadata);
    const selectedMetadata = newMetadata[metaDataIndex] || {};
    const pageSections = selectedMetadata.page_section;
    const pageSectionIndex = pageSections.findIndex(
      (pageSection = {}) => pageSection.slug === pageSectionSlug
    );
    const selectedPageSection = cloneDeep(pageSections[pageSectionIndex]);
    const fieldDefinitions = selectedPageSection.field_definition;
    const fieldDefinitionIndex = fieldDefinitions.findIndex(
      (fieldDefinition = {}) =>
        fieldDefinition.field_order.toString() === inputIndex.toString()
    );
    const selectedFieldDefinition = cloneDeep(fieldDefinitions[fieldDefinitionIndex]);
    selectedFieldDefinition.field_value =
      type === "checkbox" ? checked : value === "" ? null : value;
    fieldDefinitions[fieldDefinitionIndex] = selectedFieldDefinition;
    selectedPageSection.field_definition = fieldDefinitions;
    pageSections[pageSectionIndex] = cloneDeep(selectedPageSection);
    selectedMetadata.page_section = pageSections;
    newMetadata[metaDataIndex] = cloneDeep(selectedMetadata);

    setSensorConfig((oldObject) => ({
      ...oldObject,
      sensor: {...oldObject.sensor, metadata: newMetadata},
    }));
  };

  const handleNavClick = (metaDataIndex, pageSectionSlug) => {
    // eslint-disable-next-line react-hooks/immutability
    sensorConfig.sensor.metadata[metaDataIndex].page_section =
      sensorConfig.sensor.metadata[metaDataIndex].page_section.map((eachSection) => {
        return {...eachSection, active: eachSection.slug === pageSectionSlug};
      });
    setSensorConfig({...sensorConfig});
  };

  const routeBackToSummary = () => {
    navigate(-1);
  };

  const formatOnSubmit = (pageSections) => {
    const filteredPageSections = filterPageSections(pageSections);
    return filteredPageSections.map((pageSection = {}) => {
      const fieldDefinitions = pageSection.field_definition || [];
      const filteredFieldDefinitions = filterFieldDefinitions(fieldDefinitions);
      return {...pageSection, field_definition: filteredFieldDefinitions};
    });
  };

  const handleSubmit = () => {
    const finalPayload = cloneDeep(sensorConfig);
    // Refined final object: Only data which is visible on UI.
    finalPayload.sensor.metadata = finalPayload?.sensor?.metadata.map((eachMetaData) => ({
      ...eachMetaData,
      page_section: formatOnSubmit(eachMetaData?.page_section),
    }));

    dispatch(updateSensorDetailsConfig(finalPayload, routeBackToSummary));
  };

  return (
    <SafeView>
      {isEmpty(metadata) ? (
        <EmptyView></EmptyView>
      ) : (
        <Fragment>
          {metadata.map((eachMetadata = {}, metaIndex) => {
            const pageSections = eachMetadata.page_section || [];
            const filteredPageSections = filterPageSections(pageSections);
            const navListItems = filteredPageSections.map((section) => section.name);
            const activePageSectionIndex = filteredPageSections.findIndex(
              (section = {}) => section.active
            );
            const activePageSection = filteredPageSections[activePageSectionIndex];
            const fieldDefinitions = activePageSection.field_definition || [];
            const filteredFieldDefinitions = filterFieldDefinitions(fieldDefinitions);
            const onNavClick = (index) => {
              const selectedPage = filteredPageSections[index];
              handleNavClick(metaIndex, selectedPage.slug);
            };
            return (
              <div
                className="sensor--details--container"
                key={`${metaIndex} ${eachMetadata.slug}`}
              >
                <div
                  style={{
                    color: "#525f7f",
                    fontSize: "20px",
                    marginBottom: "20px",
                  }}
                  className="roboto-bold"
                >
                  {eachMetadata.name} - Pin #{sensorConfig.pin}
                </div>
                <Row>
                  <Col xl="3">
                    <LeftNav
                      navList={navListItems}
                      title="Configurations"
                      selectedIndex={activePageSectionIndex}
                      onClickHandle={onNavClick}
                    />
                  </Col>
                  <Col className="mb-5 mb-xl-0" xl="9">
                    <Card className="shadow">
                      <CardBody>
                        <AutoForm
                          formArray={filteredFieldDefinitions}
                          title={activePageSection.name}
                          inputChangeHandler={(event) =>
                            inputChangeListener(event, activePageSection.slug, metaIndex)
                          }
                        />{" "}
                      </CardBody>
                      <CardFooter
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "flex-end",
                          flexWrap: "wrap",
                        }}
                        className="sensor--edit--button--container"
                      >
                        <SensorConfigButtons
                          pageSections={filteredPageSections}
                          metadataIndex={metaIndex}
                          handleNavClick={handleNavClick}
                          handleSubmit={handleSubmit}
                        />
                      </CardFooter>
                    </Card>
                  </Col>
                </Row>
              </div>
            );
          })}
        </Fragment>
      )}
    </SafeView>
  );
};

export default SensorDetails;
